import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  FileText,
  Volume2,
  Send,
  Bot,
  ChevronDown,
  ChevronUp,
  Mic,
  MicOff,
  Pencil,
  X,
  ChevronsLeftRight,
  PanelRightClose,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  createDirectorSession,
  subscribeDirectorEvents,
  fetchDirectorMetrics,
  describeDirectorEvent,
  toolFamily,
  seedDirectorDemo,
  recoverDirectorDemo,
  DIRECTOR_DEMO_ENABLED,
} from '@/lib/twinneeDirector';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/** Runway Developer Portal character used for embedded voice / video widget in Director chat. */
export const RUNWAY_DIRECTOR_CHARACTER_ID = 'a08b07cd-646f-47b4-bf88-2905699788b4';

/**
 * Base URL for the official Runway Character embed script, without trailing slash.
 * Full URL: `${RUNWAY_WIDGET_SCRIPT_BASE}/${RUNWAY_DIRECTOR_CHARACTER_ID}` — confirm in Developer Portal → Character → Embed.
 * Override with REACT_APP_RUNWAY_WIDGET_SCRIPT_BASE if Runway changes CDN paths.
 */
const RUNWAY_WIDGET_SCRIPT_BASE =
  (process.env.REACT_APP_RUNWAY_WIDGET_SCRIPT_BASE || 'https://dev.runwayml.com/widget/character').replace(/\/$/, '');

const LS_RUNWAY_VOICE = (storyId) => `director_runway_voice_${storyId || 'default'}`;
const LS_DOCKED_WIDE = 'director_panel_docked_wide';

function stripForTts(text) {
  return String(text || '')
    .replace(/\*\*|`|#/g, '')
    .replace(/\n+/g, ' ')
    .trim()
    .slice(0, 2400);
}

function speakAssistantText(text) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(stripForTts(text));
    u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch {
    /* ignore */
  }
}

function safeStr(x) {
  return typeof x === 'string' ? x : '';
}

function mediaUrlForPlayback(url) {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('/api/')) return `${BACKEND_ORIGIN}${url}`;
  return url;
}

const getSpeechRecognition = () => window.SpeechRecognition || window.webkitSpeechRecognition || null;

/** `layout="docked"`: fixed right, full viewport height; optional `onClose` + `dockedBackdrop` for overlay dimming. */
export function MovieDirectorPanel({
  story,
  scenes,
  /** `story` | `narration` — picks director chat API path */
  editorMode = 'story',
  /** When set, commands without an explicit scene number target this scene/segment */
  activeSceneNumber = null,
  activeSceneId = null,
  layout = 'embedded',
  onClose,
  /** Scene grid: hide docked panel entirely (parent removes mount). */
  onHide,
  dockedBackdrop = true,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chat');
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const pollRef = useRef({ active: false, timer: null });
  const [showTools, setShowTools] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [dockedWide, setDockedWide] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(LS_DOCKED_WIDE) === '1';
    } catch {
      return false;
    }
  });

  const [runwayVoiceCharacter, setRunwayVoiceCharacter] = useState(() => {
    try {
      return localStorage.getItem(LS_RUNWAY_VOICE(story?.id)) === '1';
    } catch {
      return false;
    }
  });
  const [runwayCharacterOpen, setRunwayCharacterOpen] = useState(false);
  const runwayScriptRef = useRef(null);

  // Voice input
  const [voiceSupported] = useState(() => !!getSpeechRecognition());
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const [chatMessages, setChatMessages] = useState(() => ([
    {
      role: 'assistant',
      text: 'Director AI is ready. Tell me what to change (e.g., “regenerate scene 3 video with seedance”, “make subtitles bigger”, “rewrite Alice’s line in scene 2”).',
      ts: new Date().toISOString(),
    },
  ]));

  // Twinnee Director (ADK) — shared session, live tool/Grafana trace, telemetry.
  const [directorSessionId, setDirectorSessionId] = useState(null);
  const [directorTrace, setDirectorTrace] = useState([]);
  const [telemetry, setTelemetry] = useState(null);

  const characters = useMemo(() => (Array.isArray(story?.characters) ? story.characters : []), [story?.characters]);

  // Create/resume one ADK session per project (shared with the dashboard chat).
  useEffect(() => {
    const entityId = story?.id;
    if (!entityId) return;
    let cancelled = false;
    const isNarration = editorMode === 'narration' || story?.story_type === 'narration';
    (async () => {
      try {
        const existing =
          (typeof story?.director_session_id === 'string' && story.director_session_id) || undefined;
        const data = await createDirectorSession({
          projectId: entityId,
          surface: 'editor',
          editorMode: isNarration ? 'narration' : 'story',
          sessionId: existing,
        });
        if (!cancelled) setDirectorSessionId(data?.session_id || null);
      } catch {
        /* session is best-effort; chat still works without it */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [story?.id, story?.story_type, story?.director_session_id, editorMode]);

  // Live stream of the Director's tool calls + Grafana findings.
  useEffect(() => {
    if (!directorSessionId) return undefined;
    const unsub = subscribeDirectorEvents(directorSessionId, (evt) => {
      if (!evt || !evt.type) return;
      if (evt.type === 'tool_call' || evt.type === 'tool_result' ||
          evt.type?.startsWith('autopilot_')) {
        const label = describeDirectorEvent(evt);
        if (!label) return;
        setDirectorTrace((prev) => [
          ...prev.slice(-40),
          { id: `${evt.ts || Date.now()}_${Math.random().toString(36).slice(2, 7)}`, label, family: toolFamily(evt.tool) },
        ]);
      }
    });
    return unsub;
  }, [directorSessionId]);

  // Production telemetry strip (Grafana-backed snapshot).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await fetchDirectorMetrics();
        if (active) setTelemetry(data?.snapshot || null);
      } catch {
        /* ignore */
      }
    };
    load();
    const t = setInterval(load, 8000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [story?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_RUNWAY_VOICE(story?.id), runwayVoiceCharacter ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [runwayVoiceCharacter, story?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_DOCKED_WIDE, dockedWide ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [dockedWide]);

  useEffect(() => {
    if (!runwayCharacterOpen || !runwayVoiceCharacter) {
      if (runwayScriptRef.current) {
        try {
          runwayScriptRef.current.remove();
        } catch {
          /* ignore */
        }
        runwayScriptRef.current = null;
      }
      return undefined;
    }
    const scriptId = 'runway-director-character-widget';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `${RUNWAY_WIDGET_SCRIPT_BASE}/${RUNWAY_DIRECTOR_CHARACTER_ID}`;
    script.onerror = () => {
      toast.error('Runway character widget failed to load. Check allowed origins in Runway Developer Portal and script URL (REACT_APP_RUNWAY_WIDGET_SCRIPT_BASE).');
    };
    document.body.appendChild(script);
    runwayScriptRef.current = script;
    return () => {
      try {
        script.remove();
      } catch {
        /* ignore */
      }
      if (runwayScriptRef.current === script) runwayScriptRef.current = null;
    };
  }, [runwayCharacterOpen, runwayVoiceCharacter]);

  const sceneThumbs = useMemo(() => {
    const rows = Array.isArray(scenes) ? scenes : [];
    return rows.slice(0, 120).map((s, idx) => {
      const n = s?.scene_number ?? s?.segment_number ?? idx + 1;
      return {
        id: s?.id,
        n,
        title: safeStr(s?.scene_title || s?.title || '').trim(),
        image: mediaUrlForPlayback(s?.image_url || ''),
        video: mediaUrlForPlayback(s?.video_url || ''),
        hasAudio: Boolean(s?.audio_url),
      };
    });
  }, [scenes]);

  const scriptSummary = useMemo(() => {
    const rows = Array.isArray(scenes) ? scenes : [];
    return rows
      .map((s, idx) => {
        const n = s?.scene_number ?? s?.segment_number ?? idx + 1;
        const title = safeStr(s?.scene_title || s?.title || '').trim();
        const text = safeStr(s?.scene_text || s?.narration_text || s?.image_prompt || '').trim();
        const head = `Scene ${n}${title ? ` — ${title}` : ''}`;
        return `${head}\n${text ? text.slice(0, 900) : '(no script yet)'}`;
      })
      .join('\n\n');
  }, [scenes]);

  const audioSummary = useMemo(() => {
    const rows = Array.isArray(scenes) ? scenes : [];
    const withAudio = rows.filter((s) => !!safeStr(s?.audio_url).trim());
    if (!withAudio.length) return 'No scene audio yet. Generate “Scene dialogue” or story audio first.';
    return withAudio
      .slice(0, 40)
      .map((s) => `Scene ${s?.scene_number || ''}: ${safeStr(s?.audio_url)}`)
      .join('\n');
  }, [scenes]);

  const startEditCharacter = (charId) => {
    if (!charId) return;
    navigate('/characters', {
      state: { editCharacterId: charId, returnTo: `/stories/${story?.id}/edit` },
    });
  };

  const startRecording = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onstart = () => setIsRecording(true);
    rec.onerror = () => setIsRecording(false);
    rec.onend = () => setIsRecording(false);
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map((r) => r[0]?.transcript || '').join('');
      setChatInput(transcript);
    };
    recognitionRef.current = rec;
    rec.start();
  }, []);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop?.();
    setIsRecording(false);
  }, []);

  const toggleRecording = () => {
    if (runwayVoiceCharacter) {
      setRunwayCharacterOpen((o) => !o);
      return;
    }
    if (!voiceSupported) return;
    if (isRecording) stopRecording();
    else startRecording();
  };

  const sendDirectorMessage = async () => {
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChatInput('');
    setChatBusy(true);
    setChatMessages((prev) => ([...prev, { role: 'user', text: msg, ts: new Date().toISOString() }]));
    try {
      const isNarration = editorMode === 'narration' || story?.story_type === 'narration';
      const entityId = story?.id;
      const directorPath = isNarration
        ? `/narration/projects/${entityId}/director/chat`
        : `/stories/${entityId}/director/chat`;
      const { data } = await api.post(directorPath, {
        message: msg,
        session_id: directorSessionId || undefined,
        surface: 'editor',
        context: {
          active_tab: activeTab,
          runway_voice_character: Boolean(runwayVoiceCharacter),
          editor_mode: isNarration ? 'narration' : 'story',
          ...(activeSceneNumber != null ? { active_scene_number: Number(activeSceneNumber) } : {}),
          ...(activeSceneId ? { active_scene_id: String(activeSceneId) } : {}),
        },
      });
      if (data?.session_id && data.session_id !== directorSessionId) {
        setDirectorSessionId(data.session_id);
      }
      const reply = safeStr(data?.message) || 'Done.';
      setChatMessages((prev) => ([...prev, { role: 'assistant', text: reply, ts: data?.timestamp || new Date().toISOString() }]));
      if (runwayVoiceCharacter && reply && !runwayCharacterOpen) {
        speakAssistantText(reply);
      }
      if (data?.toast) toast.success(String(data.toast));

      const jobId = safeStr(data?.job_id);
      if (jobId) {
        setChatMessages((prev) => ([...prev, { role: 'assistant', text: `Tracking job ${jobId}…`, ts: new Date().toISOString() }]));
        if (pollRef.current.timer) clearInterval(pollRef.current.timer);
        pollRef.current.active = true;
        pollRef.current.timer = setInterval(async () => {
          try {
            const { data: job } = await api.get(`/jobs/${jobId}`);
            const st = safeStr(job?.status) || 'unknown';
            if (st === 'completed') {
              clearInterval(pollRef.current.timer);
              pollRef.current.timer = null;
              pollRef.current.active = false;
              setChatMessages((prev) => ([...prev, { role: 'assistant', text: `Job completed. Refresh scenes to see the new media.`, ts: new Date().toISOString() }]));
              toast.success('Director job completed');
            } else if (st === 'failed' || st === 'cancelled') {
              clearInterval(pollRef.current.timer);
              pollRef.current.timer = null;
              pollRef.current.active = false;
              const err = safeStr(job?.error_message);
              setChatMessages((prev) => ([...prev, { role: 'assistant', text: `Job ${st}${err ? `: ${err}` : ''}`, ts: new Date().toISOString() }]));
              toast.error(`Director job ${st}`);
            }
          } catch (_) {
            // ignore transient polling errors
          }
        }, 3000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Director chat failed');
      setChatMessages((prev) => ([...prev, { role: 'assistant', text: 'I hit an error. Try again.', ts: new Date().toISOString() }]));
    } finally {
      setChatBusy(false);
    }
  };

  useEffect(() => {
    const poll = pollRef.current;
    const rec = recognitionRef.current;
    return () => {
      if (poll?.timer) clearInterval(poll.timer);
      if (poll) {
        poll.timer = null;
        poll.active = false;
      }
      rec?.stop?.();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const panelShellStyle = { borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' };

  /** Kept outside Radix `TabsContent` so flex height + order stay correct on the Chat tab. */
  const runwayVoiceBar =
    activeTab === 'chat' ? (
      <div
        className="shrink-0 border-b px-3 pb-1.5 pt-1"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <div
          className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <TwinneeLogo className="h-4 w-4 shrink-0" style={{ color: '#f97316' }} />
            <Label
              htmlFor="runway-voice-toggle"
              className="cursor-pointer truncate text-[11px] leading-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              Runway voice character
            </Label>
          </div>
          <Switch
            id="runway-voice-toggle"
            checked={runwayVoiceCharacter}
            onCheckedChange={(v) => {
              setRunwayVoiceCharacter(Boolean(v));
              if (!v) {
                setRunwayCharacterOpen(false);
                if (typeof window !== 'undefined' && window.speechSynthesis) {
                  try {
                    window.speechSynthesis.cancel();
                  } catch {
                    /* ignore */
                  }
                }
              }
            }}
          />
        </div>
      </div>
    ) : null;

  const collapsedInner = (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden" style={{ background: 'var(--bg-secondary)' }}>
      {onClose || (layout === 'docked' && onHide) ? (
        <div
          className="flex shrink-0 justify-end gap-0.5 border-b px-1 py-1"
          style={{ borderColor: 'var(--glass-border)' }}
        >
          {layout === 'docked' && onHide ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => onHide()}
              style={{ color: 'var(--text-secondary)' }}
              title="Hide Movie Director side panel"
              data-testid="movie-director-collapsed-hide-btn"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          ) : null}
          {onClose ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={onClose}
              style={{ color: 'var(--text-secondary)' }}
              title="Close Movie Director"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        className="flex min-h-0 flex-1 flex-col items-center gap-2 border-0 bg-transparent px-1 py-4 hover:opacity-90"
        style={{ color: 'var(--text-secondary)' }}
        onClick={() => setIsCollapsed(false)}
        title="Expand Movie Director"
      >
        <Bot className="h-5 w-5 shrink-0" />
        <span className="pointer-events-none max-w-[3rem] select-none break-words text-center text-[10px] font-medium leading-tight">
          Director
        </span>
      </button>
    </div>
  );

  const expandedInner = (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2" style={{ borderColor: 'var(--glass-border)' }}>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
          <Bot className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Movie Director
          </div>
          <button
            type="button"
            onClick={() => setShowTools((v) => !v)}
            className="flex items-center gap-1 truncate text-[11px]"
            style={{ color: 'var(--text-tertiary)' }}
            title="Show tools"
          >
            Tools: script, audio, regen video/image/audio, editor tweaks
            {showTools ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {layout === 'docked' && !isCollapsed ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => setDockedWide((w) => !w)}
              style={{ color: 'var(--text-secondary)' }}
              title={dockedWide ? 'Use narrower panel' : 'Expand panel width'}
            >
              <ChevronsLeftRight className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setIsCollapsed(true)}
            style={{ color: 'var(--text-secondary)' }}
            title="Collapse side panel"
          >
            <ChevronDown className="h-4 w-4 rotate-90" />
          </Button>
          {layout === 'docked' && onHide ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => onHide()}
              style={{ color: 'var(--text-secondary)' }}
              title="Hide Movie Director"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          ) : null}
          {onClose ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={onClose}
              style={{ color: 'var(--text-secondary)' }}
              title="Close Movie Director"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {showTools && (
        <div className="shrink-0 px-4 py-2 border-b text-[11px]" style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}>
          <div className="space-y-1">
            <div>- Regen scene/segment image, video (seedance, runway, veo…), or audio</div>
            <div>- Lip sync, expand image, sync A/V timing, select media version, trim</div>
            <div>- Edit dialogue, reorder scenes, subtitle style, SFX, music, export</div>
            <div>- Say “scene 3” or select a scene — active scene is used when omitted</div>
          </div>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex h-full min-h-0 flex-1 flex-col justify-start gap-0 overflow-hidden"
      >
        <div className="shrink-0 px-3 pb-0 pt-1.5">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="characters" className="gap-1.5"><Users className="w-3.5 h-3.5" />Characters</TabsTrigger>
            <TabsTrigger value="script" className="gap-1.5"><FileText className="w-3.5 h-3.5" />Script</TabsTrigger>
            <TabsTrigger value="audio" className="gap-1.5"><Volume2 className="w-3.5 h-3.5" />Audio</TabsTrigger>
            <TabsTrigger value="chat" className="gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Chat</TabsTrigger>
          </TabsList>
        </div>

        {runwayVoiceBar}

        <TabsContent
          value="characters"
          className="!mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2 pt-0 data-[state=inactive]:hidden"
        >
          <div
            className="flex min-h-0 flex-1 basis-0 flex-col justify-start overflow-y-auto overflow-x-hidden rounded-xl border"
            style={{ borderColor: 'var(--glass-border)' }}
          >
            <div className="space-y-2 p-3">
              {characters.length ? characters.map((c) => (
                <div key={c.id || c.name} className="rounded-lg border p-2" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}>
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
                      {(() => {
                        const face = c?.creative_profile?.character?.referenceFrames?.faceFront;
                        const img = face || c?.reference_image || c?.styled_photo_url || '';
                        const src = mediaUrlForPlayback(img);
                        if (!src) return null;
                        return <img alt="" src={src} className="h-full w-full object-cover" />;
                      })()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {c.name || c.id}
                      </div>
                      <div className="truncate text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        {(Array.isArray(c.personality_traits) ? c.personality_traits.join(', ') : '') || safeStr(c.description).slice(0, 120)}
                      </div>
                    </div>
                    {c?.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-lg px-2 text-[11px]"
                        style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                        onClick={() => startEditCharacter(c.id)}
                        title="Edit character"
                      >
                        <Pencil className="mr-1 h-3 w-3" /> Edit
                      </Button>
                    )}
                  </div>
                </div>
              )) : (
                <div className="p-3 text-xs" style={{ color: 'var(--text-tertiary)' }}>No characters found on this story.</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent
          value="script"
          className="!mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2 pt-0 data-[state=inactive]:hidden"
        >
          <div
            className="flex min-h-0 flex-1 basis-0 flex-col justify-start overflow-y-auto overflow-x-hidden rounded-xl border"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
          >
            <div className="p-3">
              <div className="grid grid-cols-2 gap-2">
                {sceneThumbs.map((s) => (
                  <div key={s.id || s.n} className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
                    <div className="aspect-video" style={{ background: 'var(--bg-tertiary)' }}>
                      {s.image ? (
                        <img alt="" src={s.image} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="truncate text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Scene {s.n}{s.title ? ` · ${s.title}` : ''}
                      </div>
                      <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        {s.video ? 'Has video' : 'No video'} · {s.hasAudio ? 'Has audio' : 'No audio'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Textarea
                  readOnly
                  value={scriptSummary}
                  className="min-h-[220px] rounded-xl text-xs"
                  style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent
          value="audio"
          className="!mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2 pt-0 data-[state=inactive]:hidden"
        >
          <Textarea
            readOnly
            value={audioSummary}
            className="min-h-[120px] flex-1 resize-none overflow-y-auto rounded-xl text-xs"
            style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
          />
        </TabsContent>

        <TabsContent
          value="chat"
          className="!mt-0 flex min-h-0 min-w-0 flex-1 flex-col justify-start overflow-hidden px-3 pb-2 pt-0 data-[state=inactive]:hidden"
        >
          {telemetry ? (
            <div
              className="mb-1.5 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-2.5 py-1.5 text-[10px]"
              style={{ borderColor: 'var(--glass-border)', background: 'rgba(16,185,129,0.08)', color: 'var(--text-primary)' }}
              title="Live production telemetry (Grafana). The Director reasons over these signals."
            >
              <span className="font-semibold opacity-80">Production health</span>
              <span>scenes {Math.round(telemetry.scenes_completed || 0)}</span>
              <span>success {Math.round(telemetry.success_rate ?? 100)}%</span>
              {telemetry.character_consistency != null ? (
                <span>consistency {Math.round(telemetry.character_consistency)}%</span>
              ) : null}
              <span>latency {Math.round(telemetry.avg_generation_latency_seconds || 0)}s</span>
              {telemetry.failed_generations ? (
                <span style={{ color: '#f87171' }}>failed {Math.round(telemetry.failed_generations)}</span>
              ) : null}
              {telemetry.estimated_cost_usd ? <span>${Number(telemetry.estimated_cost_usd).toFixed(2)}</span> : null}
              {DIRECTOR_DEMO_ENABLED ? (
                <span className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    className="rounded px-1.5 py-0.5 opacity-70 hover:opacity-100"
                    style={{ border: '1px solid var(--glass-border)' }}
                    onClick={async () => {
                      try {
                        await seedDirectorDemo();
                        toast.success('Demo scenario seeded (scene 17 failed)');
                      } catch {
                        toast.error('Seed failed');
                      }
                    }}
                  >
                    seed demo
                  </button>
                  <button
                    type="button"
                    className="rounded px-1.5 py-0.5 opacity-70 hover:opacity-100"
                    style={{ border: '1px solid var(--glass-border)' }}
                    onClick={async () => {
                      try {
                        await recoverDirectorDemo();
                        toast.success('Recovery applied (87→96%)');
                      } catch {
                        toast.error('Recover failed');
                      }
                    }}
                  >
                    recover
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}
          {directorTrace.length ? (
            <div
              className="mb-1.5 flex max-h-[92px] shrink-0 flex-col gap-0.5 overflow-y-auto rounded-xl border px-2.5 py-1.5 text-[10px]"
              style={{ borderColor: 'var(--glass-border)', background: 'rgba(99,102,241,0.08)', color: 'var(--text-secondary)' }}
              title="Director reasoning trace — tool calls and Grafana queries"
            >
              <div className="mb-0.5 flex items-center justify-between">
                <span className="font-semibold opacity-80">Director trace</span>
                <button
                  type="button"
                  className="opacity-60 hover:opacity-100"
                  onClick={() => setDirectorTrace([])}
                >
                  clear
                </button>
              </div>
              {directorTrace.slice(-12).map((t) => (
                <div key={t.id} className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{
                      background:
                        t.family === 'grafana'
                          ? '#f59e0b'
                          : t.family === 'strategy'
                          ? '#a78bfa'
                          : t.family === 'qa'
                          ? '#34d399'
                          : '#60a5fa',
                    }}
                  />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</span>
                </div>
              ))}
            </div>
          ) : null}
          <div
            role="log"
            aria-live="polite"
            className="flex min-h-0 flex-1 basis-0 flex-col justify-start overflow-y-auto overflow-x-hidden rounded-xl border"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
          >
            <div className="flex flex-col justify-start gap-2 p-3">
              {chatMessages.map((m, idx) => (
                <div
                  key={`${m.ts}-${idx}`}
                  className="rounded-xl border px-3 py-2 text-xs leading-relaxed"
                  style={{
                    borderColor: 'var(--glass-border)',
                    background: m.role === 'user' ? 'rgba(249,115,22,0.12)' : 'rgba(99,102,241,0.10)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div className="mb-1 text-[10px] opacity-70">{m.role === 'user' ? 'You' : 'Director AI'}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-1.5 flex min-w-0 shrink-0 gap-2">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask the Director…"
              className="h-[44px] min-h-[44px] min-w-0 flex-1 resize-none rounded-xl text-xs"
              style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendDirectorMessage();
                }
              }}
              disabled={chatBusy}
            />
            <Button
              type="button"
              variant="outline"
              className="rounded-xl px-3"
              style={{
                borderColor: runwayVoiceCharacter ? '#f97316' : 'var(--glass-border)',
                color: 'var(--text-primary)',
              }}
              onClick={toggleRecording}
              disabled={chatBusy || (!runwayVoiceCharacter && !voiceSupported)}
              title={
                runwayVoiceCharacter
                  ? (runwayCharacterOpen ? 'Close Runway character' : 'Open Runway voice character')
                  : (voiceSupported ? (isRecording ? 'Stop recording' : 'Record voice') : 'Voice not supported')
              }
            >
              {runwayVoiceCharacter ? (
                <Mic className="h-4 w-4" />
              ) : isRecording ? (
                <MicOff className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
            <Button onClick={sendDirectorMessage} disabled={chatBusy || !chatInput.trim()} className="rounded-xl" style={{ background: '#f97316', color: 'white' }}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="shrink-0 pt-0.5 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
            {runwayVoiceCharacter
              ? 'Mic: open Runway character (embed). Type below to run Director tools; assistant replies also speak when the character panel is closed.'
              : 'Tip: try “remove narrator everywhere → regenerate story audio → regenerate all seedance videos”. Mic: speech-to-text.'}
          </div>
        </TabsContent>
      </Tabs>
    </>
  );

  const runwayDialog = (
    <Dialog open={runwayCharacterOpen} onOpenChange={setRunwayCharacterOpen}>
      <DialogContent className="max-w-lg" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }}>Runway voice character</DialogTitle>
          <DialogDescription style={{ color: 'var(--text-secondary)' }}>
            Character ID {RUNWAY_DIRECTOR_CHARACTER_ID}. The Runway widget usually appears in a corner of the page.
            Add this app&apos;s origin (including port) under Allowed origins in the Runway Developer Portal → your Character → Embed.
          </DialogDescription>
        </DialogHeader>
        <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
          Use the widget to talk with Runway&apos;s avatar. Use the chat field behind this dialog to instruct Movie Director
          (regenerate scenes, subtitles, etc.)—those actions still run through Twin Tales.
        </p>
      </DialogContent>
    </Dialog>
  );

  const expandedChrome = (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col justify-start overflow-hidden">
      {expandedInner}
    </div>
  );

  if (layout === 'docked') {
    const showBackdrop = Boolean(dockedBackdrop && onClose && !isCollapsed);
    return (
      <>
        {showBackdrop ? (
          <button
            type="button"
            className="fixed inset-0 z-[95] cursor-default border-0 bg-black/40 p-0"
            onClick={onClose}
            aria-label="Close Movie Director"
          />
        ) : null}
        <aside
          role="complementary"
          aria-label="Movie Director"
          className={`fixed right-0 top-0 z-[100] flex min-h-0 flex-col items-stretch justify-start overflow-hidden border-l shadow-2xl isolate ${
            isCollapsed ? 'w-[52px]' : dockedWide ? 'w-[min(44rem,98vw)]' : 'w-[min(28rem,92vw)]'
          }`}
          style={{
            bottom: 0,
            height: '100vh',
            maxHeight: '100dvh',
            borderColor: 'var(--glass-border)',
            background: 'var(--bg-secondary)',
          }}
        >
          {isCollapsed ? collapsedInner : expandedChrome}
        </aside>
        {runwayDialog}
      </>
    );
  }

  return (
    <>
      <div
        className="flex h-full max-h-full min-h-0 flex-col overflow-hidden border-l"
        style={panelShellStyle}
      >
        {isCollapsed ? collapsedInner : expandedChrome}
      </div>
      {runwayDialog}
    </>
  );
}

